// ✅ src/pages/OrderManagement/AddPhotoDocument.jsx
// Updated with localStorage common headers

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FaBars,
  FaHome,
  FaCamera,
  FaArrowLeft,
  FaUpload,
  FaTrash,
  FaSyncAlt,
  FaImages,
  FaEye,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "https://nissi-india.com/api/projectPhotos";

const toInt = (v, def = 0) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

const niceBytes = (bytes) => {
  const b = Number(bytes || 0);
  if (!b) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = b;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export default function AddPhotoDocument() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const contractId = useMemo(
    () =>
      toInt(
        qs.get("contractId") ||
          qs.get("contractid") ||
          sessionStorage.getItem("Selcontractid") ||
          0
      ),
    [qs]
  );

  const folderId = useMemo(
    () => toInt(qs.get("folderId") || qs.get("folid") || 0),
    [qs]
  );

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  useEffect(() => {
    if (contractId) sessionStorage.setItem("Selcontractid", String(contractId));
  }, [contractId]);

  const [loading, setLoading] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [images, setImages] = useState([]); // { imageId, imageName, url }
  const [selected, setSelected] = useState(null); // { imageId, imageName, url }

  const pickRef = useRef(null);
  const [pickedFiles, setPickedFiles] = useState([]); // File[]
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadFolderInfo = async () => {
    if (!contractId) return toast.error("contractId missing");
    if (!folderId) return toast.error("folderId missing");

    setLoading(true);
    try {
      const res = await axios.get(`${API}/folder-info`, {
        params: { contractId, folderId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Folder info failed");
        return;
      }

      setFolderName(res.data?.folderName || "");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async () => {
    if (!folderId) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API}/images`, {
        params: { folderId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Load images failed");
        return;
      }

      const list = (res.data?.images || []).map((x) => ({
        imageId: x.imageId,
        imageName: x.imageName,
        url: `https://nissi-india.com${x.url}`,
      }));

      setImages(list);
      setSelected((prev) =>
        prev && list.some((i) => i.imageId === prev.imageId) ? prev : null
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!folderId) return;
    loadFolderInfo();
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, folderId]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    setPickedFiles(files);
    setUploadProgress(0);
  };

  const upload = async () => {
    if (!contractId) return toast.error("contractId missing");
    if (!folderId) return toast.error("folderId missing");
    if (!loginUserId) return toast.error("loginUserId missing in localStorage");
    if (!pickedFiles.length) return toast.error("Please choose at least 1 image");

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("contractId", String(contractId));
      fd.append("folderId", String(folderId));
      fd.append("loginUserId", String(loginUserId));
      pickedFiles.forEach((f) => fd.append("images", f));

      const res = await axios.post(`${API}/upload`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (p) => {
          const total = p.total || 0;
          if (!total) return;
          const pct = Math.round((p.loaded / total) * 100);
          setUploadProgress(pct);
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Upload failed");
        return;
      }

      toast.success(res.data?.message || "Uploaded Successfully");
      setPickedFiles([]);
      if (pickRef.current) pickRef.current.value = "";
      setUploadProgress(0);
      await loadImages();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const removeSelected = async () => {
    if (!selected?.imageId) return toast.error("Select an image first");
    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    setLoading(true);
    try {
      const res = await axios.delete(`${API}/image/${selected.imageId}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success("Deleted");
      setSelected(null);
      await loadImages();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (selected) {
      setSelected(null);
      return;
    }
    navigate(`/OrderManagement/PhotoDocumentation?contractId=${contractId || ""}`);
  };

  const ProgressBar = ({ value }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="h-2 rounded-full transition-all duration-300 bg-blue-500"
        style={{ width: `${value}%` }}
      />
    </div>
  );

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            <button
              onClick={handleBack}
              className={`w-full flex items-center gap-3 p-3 rounded-lg mb-2 transition-all duration-200 ${
                isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-700"
              }`}
            >
              <FaArrowLeft />
              {sidebarOpen && (
                <span className="text-sm font-medium">
                  {selected ? "Back to Grid" : "Back to Folders"}
                </span>
              )}
            </button>

            <button
              onClick={loadImages}
              disabled={loading}
              className={`w-full flex items-center gap-3 p-3 rounded-lg mb-2 transition-all duration-200 ${
                loading
                  ? "bg-blue-400 text-white cursor-not-allowed"
                  : isDark
                  ? "hover:bg-gray-700 text-gray-300"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
            >
              <FaSyncAlt />
              {sidebarOpen && <span className="text-sm font-medium">Refresh</span>}
            </button>

            <button
              onClick={removeSelected}
              disabled={loading || !selected?.imageId}
              className={`w-full flex items-center gap-3 p-3 rounded-lg mb-2 transition-all duration-200 ${
                loading || !selected?.imageId
                  ? "bg-rose-400/60 text-white cursor-not-allowed"
                  : "bg-rose-600 hover:bg-rose-700 text-white"
              }`}
            >
              <FaTrash />
              {sidebarOpen && <span className="text-sm font-medium">Delete Selected</span>}
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="flex-1">
          <div
            className={`transition-all duration-300 ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1 className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      <FaCamera className="text-blue-500" />
                      IMAGE GALLERY
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Folder: <b>{folderName || "—"}</b> • ContractId: <b>{contractId || "-"}</b> • FolderId: <b>{folderId || "-"}</b>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleBack}
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaArrowLeft />
                      {selected ? "Grid" : "Folders"}
                    </button>

                    <button
                      disabled={loading}
                      onClick={() => pickRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaUpload />
                      Choose Images
                    </button>

                    <button
                      disabled={loading || !pickedFiles.length}
                      onClick={upload}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading || !pickedFiles.length
                          ? "bg-emerald-500/60 text-white cursor-not-allowed"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                    >
                      <FaImages />
                      Upload
                    </button>
                  </div>
                </div>

                <input
                  ref={pickRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*"
                  onChange={onPick}
                />
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Upload card */}
              <div className={`rounded-2xl shadow-xl border ${isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">UPLOAD IMAGE</h2>
                </div>

                <div className="p-6 space-y-3">
                  <div className={`rounded-lg border px-4 py-3 ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-gray-50"}`}>
                    <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      {pickedFiles.length ? (
                        <>
                          Selected: <b>{pickedFiles.length} file{pickedFiles.length > 1 ? "s" : ""}</b>{" "}
                          <span className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            ({niceBytes(pickedFiles.reduce((sum, f) => sum + (f.size || 0), 0))})
                          </span>
                        </>
                      ) : (
                        "No files selected"
                      )}
                    </div>

                    {uploadProgress > 0 ? (
                      <div className="mt-2">
                        <ProgressBar value={uploadProgress} />
                        <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {uploadProgress}% uploading...
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      disabled={loading}
                      onClick={loadImages}
                      className="px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <FaSyncAlt />
                      Refresh
                    </button>

                    <button
                      disabled={loading || !selected?.url}
                      onClick={() => selected?.url && window.open(selected.url, "_blank", "noopener,noreferrer")}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                        loading || !selected?.url
                          ? "bg-indigo-400/60 text-white cursor-not-allowed"
                          : "bg-indigo-600 hover:bg-indigo-700 text-white"
                      }`}
                    >
                      <FaEye />
                      View Selected
                    </button>

                    <button
                      disabled={loading || !selected?.imageId}
                      onClick={removeSelected}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                        loading || !selected?.imageId
                          ? "bg-rose-400/60 text-white cursor-not-allowed"
                          : "bg-rose-600 hover:bg-rose-700 text-white"
                      }`}
                    >
                      <FaTrash />
                      Delete Selected
                    </button>
                  </div>
                </div>
              </div>

              {/* Gallery */}
              <div className={`rounded-2xl shadow-xl border overflow-hidden ${isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="bg-blue-600 text-white px-6 py-3">
                  <h2 className="text-lg font-bold">{selected ? "PREVIEW" : "IMAGE GALLERY"}</h2>
                </div>

                {selected ? (
                  <div className="p-6">
                    <div className={`rounded-2xl border overflow-hidden ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-gray-50"}`}>
                      <div className="px-5 py-3 flex items-center justify-between border-b border-gray-200">
                        <div className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                          {selected.imageName}
                        </div>
                        <button
                          onClick={() => setSelected(null)}
                          className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Back to Grid
                        </button>
                      </div>

                      <div className="p-5 flex justify-center">
                        <img
                          src={selected.url}
                          alt={selected.imageName}
                          className="max-h-[70vh] object-contain rounded-xl shadow"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    {images.length === 0 ? (
                      <div className={`text-center py-10 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No images uploaded in this folder.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {images.map((img) => (
                          <button
                            key={img.imageId}
                            onClick={() => setSelected(img)}
                            className={`text-left rounded-2xl border overflow-hidden hover:shadow-xl transition-all duration-200 ${
                              isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"
                            }`}
                            type="button"
                          >
                            <div className="p-2">
                              <div className={`text-xs font-semibold truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                                {img.imageName}
                              </div>
                            </div>

                            <div className="aspect-[4/5] bg-gray-100">
                              <img src={img.url} alt={img.imageName} className="w-full h-full object-cover" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={`p-5 rounded-2xl shadow-xl border ${isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-700"}`}>
                <div className="font-bold mb-1">ASPX parity</div>
                <div className="text-sm">
                  Grid view = old <b>dtListImageGallery</b>. Click an image → preview mode like <b>DataList1</b>. Delete works when an image is selected.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}